/**
 * Search that forgives what people actually type (traveller tests, 2026-11-04).
 *
 * "meknes" found no Meknès and "medersa" no Médersa Bou Inania: every search
 * box compared lowercase text, accents included, and most people type without
 * accents on a phone. Both sides go through `foldForSearch`, so the query and
 * the names meet in the same plain form:
 *
 * - case and accents go (é → e, ç → c, ß → ss);
 * - Arabic vowel marks and tatweel go, and alef/yeh/teh-marbuta forms meet
 *   (أ إ آ → ا, ى → ي, ة → ه);
 * - the city names written several ways meet on one: Fez/Fes, Marrakesh,
 *   Tangier(s), Chaouen, Tetuan.
 *
 * Pure: website, app and API all use it.
 */

/** Whole words spelled several ways, folded to the one we search by. */
const SPELLINGS: Record<string, string> = {
  fez: "fes",
  marrakesh: "marrakech",
  marrakesch: "marrakech",
  marrakec: "marrakech",
  tangier: "tanger",
  tangiers: "tanger",
  chaouen: "chefchaouen",
  chefchaouene: "chefchaouen",
  tetuan: "tetouan",
  ouarzazat: "ouarzazate",
  casa: "casablanca",
};

export function foldForSearch(value: string | null | undefined): string {
  if (!value) return "";
  const plain = value
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/ß/g, "ss")
    .replace(/[ً-ٰٟـ]/g, "")
    .replace(/[أإآ]/g, "ا")
    .replace(/ى/g, "ي")
    .replace(/ة/g, "ه")
    .toLowerCase()
    .replace(/[’'`-]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return plain
    .split(" ")
    .map((w) => SPELLINGS[w] ?? w)
    .join(" ");
}

/**
 * Whether every word of the query is somewhere in the text, ignoring case,
 * accents and the spellings above. An empty query matches everything.
 */
export function searchMatches(query: string | null | undefined, ...texts: (string | null | undefined)[]): boolean {
  const q = foldForSearch(query);
  if (q.length === 0) return true;
  const haystack = foldForSearch(texts.filter(Boolean).join(" "));
  return q.split(" ").every((token) => haystack.includes(token));
}

/**
 * Every spelling of one search word, folded: "fez" gives ["fes", "fez"].
 * For a database match, where the stored text is not folded the same way.
 */
export function searchVariants(token: string): string[] {
  const folded = foldForSearch(token);
  if (!folded) return [];
  const out = new Set([folded]);
  for (const [alt, canonical] of Object.entries(SPELLINGS)) {
    if (canonical === folded) out.add(alt);
  }
  return [...out];
}

/**
 * Characters a database match folds with `translate(lower(column), FROM, TO)`,
 * which needs no extension. Lowercase Latin letters with accents only: the
 * query side goes through `foldForSearch`.
 */
export const SQL_ACCENT_FROM = "àáâãäåāăąçćčďèéêëēėęěìíîïīįñńňòóôõöøōőùúûüūůűųýÿžźżšśşťţł";
export const SQL_ACCENT_TO = "aaaaaaaaacccdeeeeeeeeiiiiiinnnoooooooouuuuuuuuyyzzzsssttl";
